"use client";
import { ContentLink } from "@components/cms/link/ContentLink";
import type { StoryTileModel } from "@lib/contentful/mappers/parts/storyTile/types";
import { handleKeyboardActivation } from "@lib/utils/a11y/keyboardActivation";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { NextImage } from "@ecco/ui/next-image";
import { useInView } from "@ecco/ui/use-in-view";
import { useEffect, useState, type FC, type KeyboardEvent } from "react";
import { listItemStyle, styles } from "./OverlayStoryListStyles";

const AUTOPLAY_INTERVAL_MS = 3000;
const VIEW_TRACKING_DELAY_MS = 2000;

export interface IOverlayStoryListProps {
    title?: string;
    storyTiles: StoryTileModel[];
    onTileInView?: (storyTile: StoryTileModel, index: number) => void;
    textColor?: string;
}

export const OverlayStoryList: FC<IOverlayStoryListProps> = ({
    title,
    storyTiles = [],
    onTileInView,
    textColor,
}) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [loadedIndices, setLoadedIndices] = useState(() => new Set([0]));
    const { ref, inView } = useInView({ threshold: 0.1 });

    useEffect(() => {
        if (isHovering || !inView || storyTiles.length < 2) return;

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % storyTiles.length);
        }, AUTOPLAY_INTERVAL_MS);

        return () => clearInterval(interval);
    }, [isHovering, inView, storyTiles.length]);

    useEffect(() => {
        const nextIndex = (activeIndex + 1) % storyTiles.length;
        setLoadedIndices((prev) => {
            if (prev.has(activeIndex) && prev.has(nextIndex)) return prev;
            return new Set(prev).add(activeIndex).add(nextIndex);
        });
    }, [activeIndex, storyTiles.length]);

    useEffect(() => {
        if (!inView || !onTileInView || !storyTiles[activeIndex]) return;

        const timeout = setTimeout(() => {
            onTileInView(storyTiles[activeIndex], activeIndex);
        }, VIEW_TRACKING_DELAY_MS);

        return () => clearTimeout(timeout);
    }, [activeIndex, inView, onTileInView, storyTiles]);

    return (
        <Flex ref={ref} sx={styles.wrapper}>
            <Flex sx={styles.listColumn}>
                {title && (
                    <Box as="h2" sx={{ ...styles.title, color: textColor }}>
                        {title}
                    </Box>
                )}
                <Flex sx={styles.list}>
                    {storyTiles.map((storyTile, i) => {
                        const { label, headline, title: tileTitle, link, links } = storyTile;
                        const text = label || headline || tileTitle;
                        const targetLink = link || links?.[0];
                        const isActive = i === activeIndex;

                        const activate = () => {
                            setIsHovering(true);
                            setActiveIndex(i);
                        };
                        const deactivate = () => setIsHovering(false);

                        const itemProps = {
                            onMouseEnter: activate,
                            onMouseLeave: deactivate,
                            onFocus: activate,
                            onBlur: deactivate,
                            onKeyDown: (e: KeyboardEvent<HTMLElement>) =>
                                handleKeyboardActivation(e, () => setActiveIndex(i)),
                            onClick: () => setActiveIndex(i),
                            sx: listItemStyle(isActive, textColor),
                        };

                        return targetLink ? (
                            <Box
                                as={ContentLink}
                                key={storyTile.id}
                                link={targetLink}
                                {...itemProps}
                                sx={{ ...itemProps.sx, ...styles.listItemLink }}
                            >
                                {text}
                            </Box>
                        ) : (
                            <Box
                                as="button"
                                type="button"
                                tabIndex={0}
                                key={storyTile.id}
                                {...itemProps}
                            >
                                {text}
                            </Box>
                        );
                    })}
                </Flex>
            </Flex>

            <Box sx={styles.imageColumn}>
                {storyTiles.map((storyTile, i) => {
                    const { image } = storyTile;

                    if (!image || !loadedIndices.has(i)) return null;

                    return (
                        <Box
                            key={storyTile.id}
                            sx={styles.imageLayer}
                            opacity={i === activeIndex ? 1 : 0}
                        >
                            <NextImage
                                src={image.url}
                                alt={image.alt}
                                sizes={["100vw", "60vw"]}
                                fill
                                objectFit="cover"
                            />
                        </Box>
                    );
                })}
            </Box>
        </Flex>
    );
};
